/**
 * Decide HOW a video gets encoded in this browser before any frame is drawn.
 *
 * Ladder, best first:
 *   1. WebCodecs H.264 (High → Main → Baseline profile) into MP4
 *   2. WebCodecs VP9 into WebM (Firefox: no AVC encoder on most platforms)
 *   3. MediaRecorder over canvas.captureStream(), real time, capped at 720p
 *
 * AV1 is deliberately excluded: software AV1 encode is an order of magnitude
 * too slow for a 15 s vertical reel.
 *
 * Browser probing is injected (`EncoderProbe`) so the ladder itself is pure
 * and unit-tested; `browserProbe()` is the real implementation.
 */

import type {
  VideoContainer,
  VideoEncoderBackend,
  VideoExportSettings,
  VideoFps,
} from '@canvas/shared-types';
import { pickMimeType } from '@/lib/createKenBurnsReel';
import { VideoExportUnsupportedError } from './exportErrors';
import { MEDIARECORDER_MAX_SIDE } from './mediaRecorderEncoder';

export interface EncoderPlan {
  backend: VideoEncoderBackend;
  container: VideoContainer;
  /** 'avc1.640028' | 'vp09.00.10.08' | a MediaRecorder mime type. */
  videoCodecString: string;
  /** 'mp4a.40.2' | 'opus'. Unset when audio is off or unsupported. */
  audioCodecString?: string;
  hardwareAcceleration: 'prefer-hardware' | 'no-preference';
  width: number;
  height: number;
  fps: VideoFps;
  videoBitrate: number;
  /** User-facing explanation when we downgraded from the request. */
  reason?: string;
}

export interface EncoderProbe {
  hasWebCodecs(): boolean;
  videoSupported(config: VideoEncoderConfig): Promise<boolean>;
  audioSupported(config: AudioEncoderConfig): Promise<boolean>;
  /** Result of the MediaRecorder mime ladder, or undefined when it can't record. */
  mediaRecorderMime(): string | undefined;
}

/** Long-edge cap for the frame-exact WebCodecs path. */
export const WEBCODECS_MAX_SIDE = 2160;
/** Pixel-rate budget: 1080p60 is the most we ask an encoder to sustain. */
export const PIXEL_RATE_BUDGET = 1920 * 1080 * 60;
/** MediaRecorder bitrates above this mostly buy dropped frames. */
const MEDIARECORDER_MAX_BITRATE = 5_000_000;

const VP9_CODEC = 'vp09.00.10.08';
const AAC_CODEC = 'mp4a.40.2';

/** H.264 4:2:0 requires even dimensions. */
export function evenDim(x: number): number {
  return Math.max(2, Math.round(x / 2) * 2);
}

/** AVC profile_idc + constraint byte, as the first 4 hex digits of the codec string. */
const AVC_PROFILES = [
  { hex: '6400', label: 'High' },
  { hex: '4d00', label: 'Main' },
  { hex: '4200', label: 'Baseline' },
] as const;

/** [level_idc hex, max macroblocks per frame, max macroblocks per second]. */
const AVC_LEVELS: Array<[string, number, number]> = [
  ['28', 8_192, 245_760], // 4.0
  ['2a', 8_704, 522_240], // 4.2
  ['34', 36_864, 2_073_600], // 5.2
];

/**
 * Codec string whose level fits the stream, so we never ship an under-spec
 * level that strict decoders refuse: <=1080p30 → 4.0, 1080p60 → 4.2, up to 4K → 5.2.
 */
export function avcCodecString(
  width: number,
  height: number,
  fps: number,
  profileHex: string = AVC_PROFILES[0].hex
): string {
  const mbPerFrame = Math.ceil(width / 16) * Math.ceil(height / 16);
  const mbPerSec = mbPerFrame * fps;
  const level =
    AVC_LEVELS.find(([, maxFrame, maxRate]) => mbPerFrame <= maxFrame && mbPerSec <= maxRate)?.[0] ??
    AVC_LEVELS[AVC_LEVELS.length - 1][0];
  return `avc1.${profileHex}${level}`;
}

/**
 * Fit a requested size inside a long-edge cap and the pixel-rate budget,
 * preserving aspect and even dimensions.
 */
export function clampOutputSize(
  width: number,
  height: number,
  fps: number,
  maxSide: number,
  pixelRateBudget = Number.POSITIVE_INFINITY
): { width: number; height: number; clamped: boolean } {
  const w = Math.max(2, width);
  const h = Math.max(2, height);
  const bySide = maxSide / Math.max(w, h);
  const byRate = Math.sqrt(pixelRateBudget / (w * h * Math.max(1, fps)));
  const scale = Math.min(1, bySide, byRate);
  const out = { width: evenDim(w * scale), height: evenDim(h * scale) };
  return { ...out, clamped: out.width !== evenDim(w) || out.height !== evenDim(h) };
}

/** Bitrate scaled down with the pixel count when we shrink the output. */
function scaledBitrate(settings: VideoExportSettings, width: number, height: number): number {
  const ratio = (width * height) / Math.max(1, settings.width * settings.height);
  return Math.max(250_000, Math.round(settings.videoBitrate * Math.min(1, ratio)));
}

function sizeLabel(w: number, h: number): string {
  return `${w}×${h}`;
}

async function planMediaRecorder(
  settings: VideoExportSettings,
  probe: EncoderProbe,
  why: string | undefined
): Promise<EncoderPlan> {
  const mime = probe.mediaRecorderMime();
  if (!mime) {
    throw new VideoExportUnsupportedError(
      'This browser can’t create video files. Use a recent Chrome or Edge to export.'
    );
  }
  const size = clampOutputSize(settings.width, settings.height, settings.fps, MEDIARECORDER_MAX_SIDE);
  const notes = [why];
  if (size.clamped) {
    notes.push(
      `Real-time recording is limited to ${MEDIARECORDER_MAX_SIDE}p, so the video will be ` +
        `${sizeLabel(size.width, size.height)} instead of ${sizeLabel(settings.width, settings.height)}.`
    );
  }
  return {
    backend: 'mediarecorder',
    container: mime.includes('mp4') ? 'mp4' : 'webm',
    videoCodecString: mime,
    hardwareAcceleration: 'no-preference',
    width: size.width,
    height: size.height,
    fps: settings.fps,
    videoBitrate: Math.min(MEDIARECORDER_MAX_BITRATE, scaledBitrate(settings, size.width, size.height)),
    reason: notes.filter(Boolean).join(' ') || undefined,
  };
}

/** Run the probe ladder and return the best plan this browser can execute. */
export async function planEncoder(
  settings: VideoExportSettings,
  probe: EncoderProbe = browserProbe()
): Promise<EncoderPlan> {
  if (settings.backend === 'mediarecorder') return planMediaRecorder(settings, probe, undefined);
  if (!probe.hasWebCodecs()) {
    return planMediaRecorder(
      settings,
      probe,
      'This browser can’t encode video frame by frame, so it will record in real time instead.'
    );
  }

  const size = clampOutputSize(
    settings.width,
    settings.height,
    settings.fps,
    WEBCODECS_MAX_SIDE,
    PIXEL_RATE_BUDGET
  );
  const { width, height } = size;
  const videoBitrate = scaledBitrate(settings, width, height);
  const sizeNote = size.clamped
    ? `Exporting at ${sizeLabel(width, height)} (the largest size this frame rate allows).`
    : undefined;
  const base = { width, height, fps: settings.fps, videoBitrate };

  const tryVideo = async (codec: string, avc: boolean) => {
    for (const hardwareAcceleration of ['prefer-hardware', 'no-preference'] as const) {
      const ok = await probe
        .videoSupported({
          codec,
          width,
          height,
          framerate: settings.fps,
          bitrate: videoBitrate,
          hardwareAcceleration,
          ...(avc ? { avc: { format: 'avc' as const } } : {}),
        })
        .catch(() => false);
      if (ok) return hardwareAcceleration;
    }
    return null;
  };

  const tryAudio = async (codec: string): Promise<string | undefined> => {
    if (!settings.includeAudio) return undefined;
    const ok = await probe
      .audioSupported({
        codec,
        sampleRate: settings.audioSampleRate,
        numberOfChannels: 2,
        bitrate: settings.audioBitrate,
      })
      .catch(() => false);
    return ok ? codec : undefined;
  };

  if (settings.container !== 'webm') {
    for (const profile of AVC_PROFILES) {
      const codec = avcCodecString(width, height, settings.fps, profile.hex);
      const accel = await tryVideo(codec, true);
      if (accel) {
        return {
          ...base,
          backend: 'webcodecs',
          container: 'mp4',
          videoCodecString: codec,
          audioCodecString: await tryAudio(AAC_CODEC),
          hardwareAcceleration: accel,
          reason: sizeNote,
        };
      }
    }
  }

  const vp9 = await tryVideo(VP9_CODEC, false);
  if (vp9) {
    const downgraded =
      settings.container !== 'webm'
        ? 'This browser can’t encode MP4 (H.264), so the video will be WebM (VP9). ' +
          'It plays in Chrome, Firefox and VLC; some apps, such as QuickTime, may not open it.'
        : undefined;
    return {
      ...base,
      backend: 'webcodecs',
      container: 'webm',
      videoCodecString: VP9_CODEC,
      audioCodecString: await tryAudio('opus'),
      hardwareAcceleration: vp9,
      reason: [downgraded, sizeNote].filter(Boolean).join(' ') || undefined,
    };
  }

  return planMediaRecorder(
    settings,
    probe,
    'This browser has no compatible frame-by-frame video encoder, so it will record in real time instead.'
  );
}

/** Settings rewritten to what the plan will actually produce. */
export function applyPlan(settings: VideoExportSettings, plan: EncoderPlan): VideoExportSettings {
  return {
    ...settings,
    width: plan.width,
    height: plan.height,
    fps: plan.fps,
    videoBitrate: plan.videoBitrate,
    container: plan.container,
    videoCodec: plan.backend === 'webcodecs' ? (plan.container === 'mp4' ? 'avc' : 'vp9') : settings.videoCodec,
    backend: plan.backend,
  };
}

/** Real browser probe. */
export function browserProbe(): EncoderProbe {
  return {
    hasWebCodecs: () =>
      typeof window !== 'undefined' &&
      'VideoEncoder' in window &&
      typeof (window as { VideoFrame?: unknown }).VideoFrame === 'function',
    videoSupported: async (config) =>
      Boolean((await VideoEncoder.isConfigSupported(config)).supported),
    audioSupported: async (config) =>
      typeof AudioEncoder !== 'undefined' &&
      Boolean((await AudioEncoder.isConfigSupported(config)).supported),
    mediaRecorderMime: () => pickMimeType(),
  };
}
